import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCamera, FiDownload, FiShare2, FiRotateCcw, FiMapPin, FiRefreshCw } = FiIcons;

const SelfieApp = () => {
  const { cityId } = useParams();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const photoContainerRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [config, setConfig] = useState(null);
  const [location, setLocation] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [facingMode, setFacingMode] = useState('user');
  const [cityInfo, setCityInfo] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [locationName, setLocationName] = useState('');
  const [shareButtonClicked, setShareButtonClicked] = useState(false);
  const [browserInfo, setBrowserInfo] = useState({
    isIOS: false,
    isSafari: false,
    isChrome: false,
    isMacSafari: false,
    isMobile: false,
    isOldMac: false
  });
  const [isCapturing, setIsCapturing] = useState(false);
  const [cameraPermission, setCameraPermission] = useState('prompt'); // 'prompt', 'granted', 'denied'
  const [appLoaded, setAppLoaded] = useState(false);
  const [initialLoadError, setInitialLoadError] = useState(null);
  const [loadingPhase, setLoadingPhase] = useState('initializing'); // Tracks detailed loading phases
  const [hasAttemptedReload, setHasAttemptedReload] = useState(false);
  const [configTimestamp, setConfigTimestamp] = useState(null);
  const [configSource, setConfigSource] = useState('');

  // Function to detect browser and OS
  const detectBrowser = () => {
    const ua = navigator.userAgent;
    const isMac = /Macintosh/.test(ua);
    // Check for older Mac OS versions
    const isOldMac = isMac && !(/Version\/1[5-9]/.test(ua) || /Version\/[2-9][0-9]/.test(ua));
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);
    const isChrome = /Chrome/.test(ua) && !/Edge/.test(ua);
    const isMacSafari = isMac && isSafari;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    
    console.log('Browser detection:', { isIOS, isSafari, isChrome, isMacSafari, isMobile, isOldMac, ua });
    setBrowserInfo({ isIOS, isSafari, isChrome, isMacSafari, isMobile, isOldMac });
    return { isIOS, isSafari, isChrome, isMacSafari, isMobile, isOldMac };
  };

  // Force reload function to clear cache
  const forceReload = () => {
    // Clear localStorage cache for this specific city
    localStorage.removeItem(`cityshot_config_${cityId}`);
    
    // Clear cache by adding a timestamp parameter to the URL
    const timestamp = new Date().getTime();
    const currentUrl = window.location.href;
    const hasParams = currentUrl.includes('?');
    const newUrl = hasParams 
      ? `${currentUrl}&_t=${timestamp}&forceClear=true` 
      : `${currentUrl}?_t=${timestamp}&forceClear=true`;
    
    window.location.href = newUrl;
  };

  // Anti-cache measures for Safari
  useEffect(() => {
    // Add no-cache headers via meta tags
    const metaCache = document.createElement('meta');
    metaCache.httpEquiv = 'Cache-Control';
    metaCache.content = 'no-cache, no-store, must-revalidate';
    document.head.appendChild(metaCache);

    const metaPragma = document.createElement('meta');
    metaPragma.httpEquiv = 'Pragma';
    metaPragma.content = 'no-cache';
    document.head.appendChild(metaPragma);

    const metaExpires = document.createElement('meta');
    metaExpires.httpEquiv = 'Expires';
    metaExpires.content = '0';
    document.head.appendChild(metaExpires);

    return () => {
      document.head.removeChild(metaCache);
      document.head.removeChild(metaPragma);
      document.head.removeChild(metaExpires);
    };
  }, []);

  // Delayed initialization to prevent white screen
  useEffect(() => {
    // Detect if we're coming from a reload attempt
    const urlParams = new URLSearchParams(window.location.search);
    const hasReloadParam = urlParams.has('_t');
    const forceClear = urlParams.has('forceClear');
    setHasAttemptedReload(hasReloadParam);

    // Add a longer delay for iOS Safari to stabilize
    const browser = detectBrowser();
    const initDelay = (browser.isIOS || browser.isMacSafari) ? 500 : 100;

    // Delay initialization to allow the DOM to stabilize
    const initTimer = setTimeout(() => {
      initializeApp(forceClear);
    }, initDelay);

    // Small delay to allow render cycle to complete
    return () => clearTimeout(initTimer);
  }, []);

  // Main app initialization function
  const initializeApp = async (forceClear = false) => {
    try {
      setLoadingPhase('detecting-browser');
      const browser = detectBrowser();

      setLoadingPhase('loading-config');
      // Load city configuration with server sync
      await loadConfiguration(forceClear);

      setLoadingPhase('loading-city-info');
      // Get city info from local storage
      const cities = JSON.parse(localStorage.getItem('cityshot_cities') || '[]');
      const city = cities.find(c => c.id === cityId);
      if (city) {
        setCityInfo(city);
      }

      // Check if camera access is allowed in Safari
      if ((browser.isIOS || browser.isMacSafari) && 'permissions' in navigator) {
        try {
          setLoadingPhase('checking-permissions');
          const permissionStatus = await navigator.permissions.query({ name: 'camera' });
          console.log('Camera permission state:', permissionStatus.state);
          setCameraPermission(permissionStatus.state);

          // Listen for permission changes
          permissionStatus.onchange = () => {
            console.log('Camera permission changed:', permissionStatus.state);
            setCameraPermission(permissionStatus.state);
          };
        } catch (err) {
          console.log('Permission API not supported or error:', err);
        }
      }

      setLoadingPhase('getting-location');
      // Get user's actual location
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            fetchLocationName(position.coords.latitude, position.coords.longitude);
            setLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              detected: true
            });
          },
          (error) => {
            console.log('Location access denied:', error);
            // Use the city name as fallback
            if (city) {
              setLocation({ detected: false, cityName: city.cityName });
              setLocationName(city.cityName);
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 10000
          }
        );
      } else {
        // Fallback if geolocation not supported
        if (city) {
          setLocation({ detected: false, cityName: city.cityName });
          setLocationName(city.cityName);
        }
      }

      // Mark app as loaded
      setLoadingPhase('complete');
      setAppLoaded(true);
      
    } catch (error) {
      console.error("Error during app initialization:", error);
      setInitialLoadError(error.message || "Failed to initialize the app. Please try refreshing the page.");
    }
  };

  // New function to load configuration with server sync
  const loadConfiguration = async (forceClear = false) => {
    // Check if we should force clear the local cache
    if (forceClear) {
      localStorage.removeItem(`cityshot_config_${cityId}`);
      console.log("Forced configuration clear");
    }
    
    try {
      // Generate a timestamp for this load
      const loadTimestamp = Date.now();
      
      // Try to load from localStorage first
      const savedConfig = localStorage.getItem(`cityshot_config_${cityId}`);
      if (savedConfig && !forceClear) {
        try {
          const parsedConfig = JSON.parse(savedConfig);
          
          // Check if the config has a timestamp and store it
          if (parsedConfig._timestamp) {
            setConfigTimestamp(parsedConfig._timestamp);
            console.log(`Loaded config from localStorage with timestamp: ${new Date(parsedConfig._timestamp).toLocaleString()}`);
          } else {
            // Add timestamp if missing
            parsedConfig._timestamp = loadTimestamp;
            localStorage.setItem(`cityshot_config_${cityId}`, JSON.stringify(parsedConfig));
            setConfigTimestamp(loadTimestamp);
            console.log(`Added timestamp ${loadTimestamp} to existing config`);
          }
          
          // Convert any absolute pixel positions to relative percentages
          const convertedConfig = convertToRelativeUnits(parsedConfig);
          setConfig(convertedConfig);
          setConfigSource('localStorage');
          return;
        } catch (error) {
          console.error("Error parsing config:", error);
          // If parsing fails, continue to default config
        }
      }
      
      // If we get here, we need to set the default config
      const defaultConfig = getDefaultConfig();
      defaultConfig._timestamp = loadTimestamp;
      localStorage.setItem(`cityshot_config_${cityId}`, JSON.stringify(defaultConfig));
      setConfig(defaultConfig);
      setConfigTimestamp(loadTimestamp);
      setConfigSource('default');
      console.log(`Created new default config with timestamp: ${loadTimestamp}`);
    } catch (error) {
      console.error("Error loading configuration:", error);
      setDefaultConfig();
    }
  };

  // Clean up resources when component unmounts
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  // Helper function to convert absolute pixel positions to relative percentages
  const convertToRelativeUnits = (configObj) => {
    if (!configObj) return null;
    const converted = { ...configObj };

    // Handle logo position
    if (typeof converted.logoPosition?.x === 'number') {
      converted.logoPosition = {
        ...converted.logoPosition,
        x: converted.logoPosition.x > 1 ? `${converted.logoPosition.x / 10}%` : converted.logoPosition.x
      };
    }
    if (typeof converted.logoPosition?.y === 'number') {
      converted.logoPosition = {
        ...converted.logoPosition,
        y: converted.logoPosition.y > 1 ? `${converted.logoPosition.y / 10}%` : converted.logoPosition.y
      };
    }

    // Handle second image position
    if (typeof converted.secondImagePosition?.x === 'number' && !['left', 'center', 'right'].includes(converted.secondImagePosition.x)) {
      converted.secondImagePosition = {
        ...converted.secondImagePosition,
        x: converted.secondImagePosition.x > 1 ? `${converted.secondImagePosition.x / 10}%` : converted.secondImagePosition.x
      };
    }
    if (typeof converted.secondImagePosition?.y === 'number') {
      converted.secondImagePosition = {
        ...converted.secondImagePosition,
        y: converted.secondImagePosition.y > 1 ? `${converted.secondImagePosition.y / 10}%` : converted.secondImagePosition.y
      };
    }

    // Convert logo and second image size to percentage of container
    if (typeof converted.logoSize === 'number' && converted.logoSize > 1) {
      converted.logoSize = `${converted.logoSize / 4}%`;
    }
    if (typeof converted.secondImageSize === 'number' && converted.secondImageSize > 1) {
      converted.secondImageSize = `${converted.secondImageSize / 4}%`;
    }

    // Convert text sizes to relative viewport units
    if (typeof converted.textSize === 'number') {
      converted.textSize = `${converted.textSize / 10}vmin`;
    }
    if (typeof converted.locationSize === 'number') {
      converted.locationSize = `${converted.locationSize / 10}vmin`;
    }
    if (typeof converted.additionalTextSize === 'number') {
      converted.additionalTextSize = `${converted.additionalTextSize / 10}vmin`;
    }

    return converted;
  };

  // Default configuration with relative units
  const getDefaultConfig = () => {
    return {
      primaryColor: '#3B82F6',
      secondaryColor: '#EF4444',
      textColor: '#FFFFFF',
      logo: '',
      logoPosition: { x: '2%', y: '2%' },
      logoSize: '20%',
      logoOpacity: 1,
      secondImage: '',
      secondImagePosition: { x: 'right', y: '2%' },
      secondImageSize: '20%',
      secondImageOpacity: 1,
      customText: 'Willkommen!',
      textPosition: { x: 'center', y: 'bottom' },
      textSize: '1.8vmin',
      textOpacity: 0.9,
      showLocation: true,
      showGreeting: true,
      showAdditionalText: false,
      greetingText: 'Viele Grüße aus',
      additionalText: '',
      locationPosition: { x: 'center', y: 'bottom' },
      locationSize: '2.2vmin',
      locationOpacity: 1,
      additionalTextPosition: { x: 'center', y: 'bottom' },
      additionalTextSize: '1.6vmin',
      additionalTextOpacity: 0.9,
      textBackground: true,
      textStyle: 'bold',
      fontFamily: 'Arial',
      _timestamp: Date.now(),
    };
  };

  const setDefaultConfig = () => {
    const defaultConfig = getDefaultConfig();
    setConfig(defaultConfig);
    setConfigTimestamp(Date.now());
    setConfigSource('default-fallback');
  };

  // Fetch location name from coordinates using reverse geocoding
  const fetchLocationName = async (lat, lng) => {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
      if (response.ok) {
        const data = await response.json();
        let locationString = '';

        // Extract the most relevant location info
        if (data.address) {
          const address = data.address;
          if (address.city) {
            locationString = address.city;
          } else if (address.town) {
            locationString = address.town;
          } else if (address.village) {
            locationString = address.village;
          } else if (address.county) {
            locationString = address.county;
          }
        }

        if (locationString) {
          setLocationName(locationString);
        } else {
          setLocationName(cityInfo?.cityName || "Unbekannter Ort");
        }
      } else {
        throw new Error('Failed to fetch location data');
      }
    } catch (error) {
      console.error("Error fetching location name:", error);
      setLocationName(cityInfo?.cityName || "Unbekannter Ort");
    }
  };

  // Safari-specific camera fix - creates a temporary video element to trigger permissions
  const prewarmSafariCamera = async () => {
    console.log('Attempting to prewarm Safari camera');
    if (browserInfo.isSafari || browserInfo.isIOS || browserInfo.isMacSafari) {
      try {
        // Create a temporary video element
        const tempVideo = document.createElement('video');
        tempVideo.setAttribute('playsinline', '');
        tempVideo.setAttribute('muted', '');
        tempVideo.style.width = '1px';
        tempVideo.style.height = '1px';
        tempVideo.style.position = 'absolute';
        tempVideo.style.opacity = '0.01';
        document.body.appendChild(tempVideo);

        // Get a temporary stream to trigger permissions
        const tempStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });

        // Assign the stream to the temporary video
        tempVideo.srcObject = tempStream;

        // Play the video (required for Safari)
        await tempVideo.play();

        // Wait a moment to ensure permissions are registered
        await new Promise(resolve => setTimeout(resolve, 500));

        // Clean up
        tempStream.getTracks().forEach(track => track.stop());
        document.body.removeChild(tempVideo);
        console.log('Safari camera prewarm successful');
        return true;
      } catch (error) {
        console.error('Safari camera prewarm failed:', error);
        return false;
      }
    }
    return true;
  };

  // Enhanced Safari and iOS camera initialization
  const startCamera = async () => {
    try {
      setCameraError(null);
      console.log('Starting camera with mode:', facingMode);

      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera API not supported in this browser');
      }

      // For Safari, try to prewarm the camera permissions
      if (browserInfo.isSafari || browserInfo.isIOS || browserInfo.isMacSafari) {
        await prewarmSafariCamera();
      }

      // Determine which constraints to use based on browser and device
      let constraints;

      // iOS Safari specific constraints - simplest possible to avoid white screen
      if (browserInfo.isIOS && browserInfo.isSafari) {
        constraints = {
          video: {
            facingMode,
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false
        };
      }
      // Older Mac Safari (pre-Monterey) needs specific constraints
      else if (browserInfo.isMacSafari && browserInfo.isOldMac) {
        constraints = {
          video: {
            facingMode,
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false
        };
      }
      // Mobile devices - use appropriate resolution
      else if (browserInfo.isMobile) {
        constraints = {
          video: {
            facingMode,
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false
        };
      }
      // Desktop browsers - high quality
      else {
        constraints = {
          video: {
            facingMode,
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          },
          audio: false
        };
      }

      console.log('Requesting camera with constraints:', constraints);

      // Get media stream with special handling for iOS Safari
      let mediaStream;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (e) {
        // If first attempt fails on iOS or old Mac Safari, try with minimal constraints
        if ((browserInfo.isIOS || (browserInfo.isMacSafari && browserInfo.isOldMac)) && 
            (e.name === 'OverconstrainedError' || e.name === 'NotReadableError')) {
          console.log('First attempt failed, trying with minimal constraints');
          mediaStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        } else {
          throw e;
        }
      }

      console.log('Camera stream acquired successfully');

      if (videoRef.current) {
        // Set attributes needed for iOS Safari
        videoRef.current.setAttribute('autoplay', '');
        videoRef.current.setAttribute('playsinline', '');
        videoRef.current.setAttribute('muted', '');
        videoRef.current.muted = true;

        // Set the stream as source
        videoRef.current.srcObject = mediaStream;

        // Safari needs to manually call play() after metadata is loaded
        videoRef.current.onloadedmetadata = async () => {
          try {
            console.log('Video metadata loaded, attempting to play');
            // Need to wait a moment for Safari
            await new Promise(resolve => setTimeout(resolve, 300));
            
            if (videoRef.current) {
              await videoRef.current.play();
              setCameraActive(true);
              console.log('Video playback started successfully');
            }
          } catch (playErr) {
            console.error("Error playing video:", playErr);
            
            // Special handling for Safari autoplay issues
            if (browserInfo.isSafari || browserInfo.isIOS || browserInfo.isMacSafari) {
              setCameraError("Tippen Sie auf den Bildschirm, um die Kamera zu aktivieren");
              
              // Add click handler to video to start playback
              if (videoRef.current) {
                const playOnClick = async () => {
                  try {
                    await videoRef.current.play();
                    setCameraActive(true);
                    setCameraError(null);
                    videoRef.current.removeEventListener('click', playOnClick);
                  } catch (err) {
                    setCameraError("Kamera-Zugriff fehlgeschlagen. Bitte prüfen Sie Ihre Browser-Einstellungen und Berechtigungen.");
                  }
                };
                videoRef.current.addEventListener('click', playOnClick);
              }
            } else {
              setCameraError("Video playback failed. Please check camera permissions.");
            }
          }
        };

        setStream(mediaStream);
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      
      // Provide detailed error messages based on browser and error
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        if (browserInfo.isIOS && browserInfo.isSafari) {
          setCameraError('Kamera-Zugriff wurde verweigert. Bitte erlauben Sie den Zugriff in den Safari-Einstellungen unter "Einstellungen > Safari > Kamera" und laden Sie die Seite neu.');
        } else if (browserInfo.isMacSafari) {
          setCameraError('Kamera-Zugriff wurde verweigert. Bitte erlauben Sie den Zugriff in den Safari-Einstellungen unter "Safari > Einstellungen > Websites > Kamera" für diese Website.');
        } else {
          setCameraError('Kamera-Zugriff wurde verweigert. Bitte erlauben Sie den Zugriff in den Browser-Einstellungen und laden Sie die Seite neu.');
        }
      } else if (error.name === 'NotFoundError') {
        setCameraError('Keine Kamera gefunden. Bitte stellen Sie sicher, dass Ihr Gerät über eine Kamera verfügt und diese nicht von einer anderen App verwendet wird.');
      } else if (error.name === 'NotReadableError' || error.name === 'AbortError') {
        setCameraError('Die Kamera kann nicht zugegriffen werden. Sie wird möglicherweise bereits von einer anderen App verwendet oder ist nicht richtig angeschlossen.');
      } else if (error.name === 'SecurityError') {
        setCameraError('Kamera-Zugriff aus Sicherheitsgründen verweigert. Diese Website muss über HTTPS aufgerufen werden.');
      } else if (error.name === 'OverconstrainedError') {
        // Try again with minimal constraints
        try {
          console.log('Trying with minimal constraints');
          const minimalStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
          if (videoRef.current) {
            videoRef.current.srcObject = minimalStream;
            videoRef.current.onloadedmetadata = () => {
              videoRef.current.play().catch(err => {
                console.error("Error playing video with minimal constraints:", err);
                setCameraError("Video playback failed. Please check camera permissions.");
              });
              setCameraActive(true);
            };
            setStream(minimalStream);
            setCameraError(null);
          }
        } catch (fallbackError) {
          setCameraError(`Die angeforderten Kamera-Einstellungen werden nicht unterstützt. Fehler: ${fallbackError.message}`);
        }
      } else {
        setCameraError(`Kamera-Fehler: ${error.message || 'Unbekannter Fehler'}`);
      }
    }
  };

  // Stop the camera
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
      setCameraActive(false);
    }
  };

  // Switch camera between front and back
  const switchCamera = async () => {
    stopCamera();
    setFacingMode(facingMode === 'user' ? 'environment' : 'user');
    // Give more time for Safari to release and reacquire the camera
    setTimeout(startCamera, browserInfo.isSafari || browserInfo.isIOS ? 500 : 300);
  };

  // High-quality capture method - prioritizes quality with progressive fallbacks
  const capturePhoto = async () => {
    setIsCapturing(true);
    try {
      if (!videoRef.current || !photoContainerRef.current) {
        throw new Error('Video or container reference not available');
      }

      // Get dimensions
      const containerWidth = photoContainerRef.current.offsetWidth;
      const containerHeight = photoContainerRef.current.offsetHeight;
      const videoWidth = videoRef.current.videoWidth;
      const videoHeight = videoRef.current.videoHeight;

      // Determine best quality settings based on device capabilities
      const pixelRatio = window.devicePixelRatio || 1;
      console.log("Capture dimensions:", {
        container: { width: containerWidth, height: containerHeight },
        video: { width: videoWidth, height: videoHeight },
        pixelRatio
      });

      // Special handling for iOS Safari - use simpler capture method
      if (browserInfo.isIOS && browserInfo.isSafari) {
        try {
          console.log('Using simplified capture for iOS Safari');
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Use device pixel ratio for better quality, but cap at 2x for performance
          const scaleFactor = Math.min(pixelRatio, 2);
          canvas.width = containerWidth * scaleFactor;
          canvas.height = containerHeight * scaleFactor;
          
          // Apply mirroring for selfie mode
          if (facingMode === 'user') {
            ctx.save();
            ctx.scale(-1, 1);
            ctx.translate(-canvas.width, 0);
            ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
            ctx.restore();
          } else {
            ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
          }

          // Add overlays manually
          await renderOverlaysDirectly(ctx, canvas.width, canvas.height);
          
          // Get as PNG for quality and transparency
          const imageData = canvas.toDataURL('image/png', 1.0);
          setCapturedImage(imageData);
          stopCamera();
          console.log('Photo captured with iOS Safari method');
          return;
        } catch (iosError) {
          console.error("iOS Safari capture failed:", iosError);
          // Fall through to other methods
        }
      }

      // High-quality capture method - uses full video resolution
      try {
        // Create canvas for high-quality capture
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas to video dimensions for best quality
        canvas.width = videoWidth;
        canvas.height = videoHeight;
        
        // Draw the video frame to the canvas with mirroring if needed
        if (facingMode === 'user') {
          ctx.save();
          ctx.scale(-1, 1);
          ctx.translate(-videoWidth, 0);
          ctx.drawImage(videoRef.current, 0, 0);
          ctx.restore();
        } else {
          ctx.drawImage(videoRef.current, 0, 0);
        }

        // Calculate center-focused crop coordinates to match container aspect ratio
        const containerRatio = containerWidth / containerHeight;
        const videoRatio = videoWidth / videoHeight;
        let sx, sy, sWidth, sHeight;
        
        if (videoRatio > containerRatio) {
          // Video is wider than container - crop sides
          sHeight = videoHeight;
          sWidth = videoHeight * containerRatio;
          sx = (videoWidth - sWidth) / 2;
          sy = 0;
        } else {
          // Video is taller than container - crop top/bottom
          sWidth = videoWidth;
          sHeight = videoWidth / containerRatio;
          sx = 0;
          sy = (videoHeight - sHeight) / 2;
        }

        // Create a new canvas with the desired output dimensions
        const outputCanvas = document.createElement('canvas');
        // Ensure high resolution output for all devices
        outputCanvas.width = Math.round(containerWidth * Math.max(pixelRatio, 2));
        outputCanvas.height = Math.round(containerHeight * Math.max(pixelRatio, 2));
        const outputCtx = outputCanvas.getContext('2d');
        
        // Draw the cropped region from the full-resolution canvas
        outputCtx.drawImage(
          canvas,
          sx, sy, sWidth, sHeight,
          0, 0, outputCanvas.width, outputCanvas.height
        );

        // Try to render overlays directly for best quality
        if (overlayRef.current) {
          try {
            const overlayCanvas = await html2canvas(overlayRef.current, {
              backgroundColor: null,
              scale: Math.max(pixelRatio, 2),
              logging: false,
              useCORS: true,
              allowTaint: true
            });
            
            // Draw the overlay canvas on top
            outputCtx.drawImage(
              overlayCanvas,
              0, 0, overlayCanvas.width, overlayCanvas.height,
              0, 0, outputCanvas.width, outputCanvas.height
            );
          } catch (overlayError) {
            console.error("Failed to capture overlay with html2canvas:", overlayError);
            // Manual overlay rendering as fallback
            await renderOverlaysDirectly(outputCtx, outputCanvas.width, outputCanvas.height);
          }
        }

        // Get the final image as PNG for best quality with transparency
        const imageData = outputCanvas.toDataURL('image/png', 1.0);
        setCapturedImage(imageData);
        stopCamera();
        console.log('Photo captured with high quality method');
        return;
      } catch (error) {
        console.error("High quality capture failed:", error);
        
        // Mid-quality fallback
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Use container dimensions with device pixel ratio
          canvas.width = Math.round(containerWidth * Math.max(pixelRatio, 2));
          canvas.height = Math.round(containerHeight * Math.max(pixelRatio, 2));
          
          // Calculate scaling to fit video to canvas
          const scale = Math.max(
            canvas.width / videoWidth,
            canvas.height / videoHeight
          );
          const scaledWidth = videoWidth * scale;
          const scaledHeight = videoHeight * scale;
          const x = (canvas.width - scaledWidth) / 2;
          const y = (canvas.height - scaledHeight) / 2;
          
          // Apply mirroring for selfie mode
          if (facingMode === 'user') {
            ctx.save();
            ctx.scale(-1, 1);
            ctx.translate(-canvas.width, 0);
            ctx.drawImage(videoRef.current, x, y, scaledWidth, scaledHeight);
            ctx.restore();
          } else {
            ctx.drawImage(videoRef.current, x, y, scaledWidth, scaledHeight);
          }

          // Add overlays manually
          await renderOverlaysDirectly(ctx, canvas.width, canvas.height);
          
          // Get as PNG for quality and transparency
          const imageData = canvas.toDataURL('image/png', 1.0);
          setCapturedImage(imageData);
          stopCamera();
          console.log('Photo captured with mid quality fallback');
          return;
        } catch (midQualityError) {
          console.error("Mid quality capture failed:", midQualityError);
          
          // Low-quality fallback
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = 1200;
          canvas.height = 1200;
          
          // Simple draw without fancy processing
          ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
          
          // Add basic text overlay
          ctx.font = 'bold 24px Arial';
          ctx.fillStyle = 'white';
          ctx.textAlign = 'center';
          ctx.shadowColor = 'black';
          ctx.shadowBlur = 4;
          ctx.fillText(`📍 ${locationName || cityInfo?.cityName || "Unbekannt"}`, canvas.width / 2, canvas.height - 30);
          
          // Still use PNG for transparency
          const imageData = canvas.toDataURL('image/png', 1.0);
          setCapturedImage(imageData);
          stopCamera();
          console.log('Photo captured with low quality fallback');
        }
      }
    } catch (error) {
      console.error('All capture methods failed:', error);
      alert('Foto aufnehmen fehlgeschlagen. Bitte versuchen Sie es erneut.');
    } finally {
      setIsCapturing(false);
    }
  };

  // Helper function to render overlays directly to canvas
  const renderOverlaysDirectly = async (ctx, width, height) => {
    if (!config) return;

    // Function to calculate position based on alignment and percentage
    const calculatePosition = (pos, dimension, alignment) => {
      if (alignment === 'center') return dimension / 2;
      if (alignment === 'right' || alignment === 'bottom') return dimension - parseFloat(pos);
      return parseFloat(pos);
    };

    // Draw logo if available
    if (config.logo) {
      try {
        const logoImg = new Image();
        logoImg.crossOrigin = "anonymous";
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
          logoImg.src = config.logo;
        });

        const logoSize = parseFloat(config.logoSize) / 100 * height;
        const logoX = typeof config.logoPosition.x === 'string' && config.logoPosition.x.endsWith('%') 
          ? parseFloat(config.logoPosition.x) / 100 * width 
          : calculatePosition(20, width, config.logoPosition.x);
        const logoY = typeof config.logoPosition.y === 'string' && config.logoPosition.y.endsWith('%') 
          ? parseFloat(config.logoPosition.y) / 100 * height 
          : calculatePosition(20, height, config.logoPosition.y);

        ctx.globalAlpha = config.logoOpacity;
        const aspectRatio = logoImg.width / logoImg.height;
        ctx.drawImage(logoImg, logoX, logoY, logoSize * aspectRatio, logoSize);
        ctx.globalAlpha = 1.0;
      } catch (error) {
        console.error("Error drawing logo:", error);
      }
    }

    // Draw second image if available
    if (config.secondImage) {
      try {
        const secondImg = new Image();
        secondImg.crossOrigin = "anonymous";
        await new Promise((resolve, reject) => {
          secondImg.onload = resolve;
          secondImg.onerror = reject;
          secondImg.src = config.secondImage;
        });

        const imgSize = parseFloat(config.secondImageSize) / 100 * height;
        let imgX;
        if (config.secondImagePosition.x === 'left') {
          imgX = 20;
        } else if (config.secondImagePosition.x === 'center') {
          imgX = width / 2;
        } else if (config.secondImagePosition.x === 'right') {
          imgX = width - 20 - (imgSize * (secondImg.width / secondImg.height));
        } else {
          imgX = parseFloat(config.secondImagePosition.x) / 100 * width;
        }

        const imgY = typeof config.secondImagePosition.y === 'string' && config.secondImagePosition.y.endsWith('%') 
          ? parseFloat(config.secondImagePosition.y) / 100 * height 
          : calculatePosition(20, height, config.secondImagePosition.y);

        ctx.globalAlpha = config.secondImageOpacity;
        const aspectRatio = secondImg.width / secondImg.height;
        ctx.drawImage(secondImg, imgX, imgY, imgSize * aspectRatio, imgSize);
        ctx.globalAlpha = 1.0;
      } catch (error) {
        console.error("Error drawing second image:", error);
      }
    }

    // Draw custom text
    if (config.customText) {
      const fontSize = parseFloat(config.textSize) * (height / 100);
      ctx.font = `${config.textStyle.includes('bold') ? 'bold' : 'normal'} ${config.textStyle.includes('italic') ? 'italic' : 'normal'} ${fontSize}px ${config.fontFamily || 'Arial'}`;
      ctx.textAlign = config.textPosition.x === 'center' ? 'center' : config.textPosition.x === 'right' ? 'right' : 'left';

      let textX;
      if (config.textPosition.x === 'center') {
        textX = width / 2;
      } else if (config.textPosition.x === 'right') {
        textX = width - 20;
      } else {
        textX = 20;
      }

      let textY;
      if (config.textPosition.y === 'middle') {
        textY = height / 2;
      } else if (config.textPosition.y === 'bottom') {
        textY = height - 20;
      } else {
        textY = 20 + fontSize;
      }

      // Draw text background if enabled
      if (config.textBackground) {
        const textWidth = ctx.measureText(config.customText).width;
        const padding = 10;
        ctx.globalAlpha = 0.5;
        ctx.fillStyle = 'black';
        if (ctx.textAlign === 'center') {
          ctx.fillRect(textX - textWidth / 2 - padding, textY - fontSize, textWidth + padding * 2, fontSize + padding);
        } else if (ctx.textAlign === 'right') {
          ctx.fillRect(textX - textWidth - padding, textY - fontSize, textWidth + padding * 2, fontSize + padding);
        } else {
          ctx.fillRect(textX - padding, textY - fontSize, textWidth + padding * 2, fontSize + padding);
        }
        ctx.globalAlpha = 1.0;
      }

      // Draw text
      ctx.globalAlpha = config.textOpacity;
      ctx.fillStyle = config.textColor || '#FFFFFF';
      ctx.fillText(config.customText, textX, textY);
      ctx.globalAlpha = 1.0;
    }

    // Draw location if enabled
    if (config.showLocation) {
      const locationFontSize = parseFloat(config.locationSize) * (height / 100);
      const greetingFontSize = locationFontSize * 0.8;

      let locationX;
      if (config.locationPosition.x === 'center') {
        locationX = width / 2;
        ctx.textAlign = 'center';
      } else if (config.locationPosition.x === 'right') {
        locationX = width - 20;
        ctx.textAlign = 'right';
      } else {
        locationX = 20;
        ctx.textAlign = 'left';
      }

      let locationY;
      if (config.locationPosition.y === 'middle') {
        locationY = height / 2;
      } else if (config.locationPosition.y === 'bottom') {
        locationY = height - 20;
      } else {
        locationY = 20 + locationFontSize;
      }

      // Calculate total height for background
      let totalHeight = locationFontSize;
      if (config.showGreeting && config.greetingText) {
        totalHeight += greetingFontSize;
      }
      if (config.showAdditionalText && config.additionalText) {
        totalHeight += parseFloat(config.additionalTextSize) * (height / 100);
      }

      // Draw background if enabled
      if (config.textBackground) {
        // Estimate width (this is approximate)
        let textWidth = ctx.measureText(locationName || cityInfo?.cityName || "Unbekannt").width + 40; // Add extra for icon
        ctx.globalAlpha = 0.5;
        ctx.fillStyle = 'black';
        if (ctx.textAlign === 'center') {
          ctx.fillRect(locationX - textWidth / 2, locationY - totalHeight, textWidth, totalHeight + 20);
        } else if (ctx.textAlign === 'right') {
          ctx.fillRect(locationX - textWidth, locationY - totalHeight, textWidth, totalHeight + 20);
        } else {
          ctx.fillRect(locationX, locationY - totalHeight, textWidth, totalHeight + 20);
        }
        ctx.globalAlpha = 1.0;
      }

      // Draw greeting text if enabled
      let currentY = locationY - totalHeight + greetingFontSize;
      if (config.showGreeting && config.greetingText) {
        ctx.font = `normal ${greetingFontSize}px ${config.fontFamily || 'Arial'}`;
        ctx.fillStyle = config.textColor || '#FFFFFF';
        ctx.fillText(config.greetingText, locationX, currentY);
        currentY += greetingFontSize + 5;
      }

      // Draw location name
      ctx.font = `bold ${locationFontSize}px ${config.fontFamily || 'Arial'}`;
      ctx.fillStyle = config.textColor || '#FFFFFF';
      ctx.fillText(`📍 ${locationName || (cityInfo?.cityName || "Unbekannt")}`, locationX, currentY);
      currentY += locationFontSize + 5;

      // Draw additional text if enabled
      if (config.showAdditionalText && config.additionalText) {
        const addTextSize = parseFloat(config.additionalTextSize) * (height / 100);
        ctx.font = `normal ${addTextSize}px ${config.fontFamily || 'Arial'}`;
        ctx.fillStyle = config.textColor || '#FFFFFF';
        ctx.fillText(config.additionalText, locationX, currentY);
      }
    }
  };

  // Download the image
  const downloadImage = () => {
    if (capturedImage) {
      try {
        // Create a link element
        const link = document.createElement('a');
        link.href = capturedImage;
        link.download = `cityshot-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
      } catch (err) {
        console.error("Error in download:", err);
        
        // Alternative method using file-saver
        try {
          fetch(capturedImage)
            .then(res => res.blob())
            .then(blob => {
              saveAs(blob, `cityshot-${Date.now()}.png`);
            })
            .catch(error => {
              console.error("SaveAs error:", error);
              alert("Download fehlgeschlagen. Bitte versuchen Sie es erneut.");
            });
        } catch (fallbackErr) {
          console.error("Fallback download error:", fallbackErr);
          alert("Download fehlgeschlagen. Bitte versuchen Sie es erneut.");
        }
      }
    }
  };

  // Share the image
  const shareImage = async () => {
    setShareButtonClicked(true);
    if (capturedImage) {
      try {
        // Convert data URL to blob
        const response = await fetch(capturedImage);
        const blob = await response.blob();
        
        // For iOS, we need to use a File object with the correct MIME type
        const file = new File([blob], 'cityshot.png', { type: 'image/png' });
        
        // Check if Web Share API is available and supports sharing files
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              title: 'Mein CityShot',
              text: 'Schau dir mein Foto an!',
              files: [file]
            });
          } catch (shareError) {
            console.error('Share error:', shareError);
            
            // If sharing files failed, try without files
            if (shareError.name !== 'AbortError') {
              try {
                await navigator.share({
                  title: 'Mein CityShot',
                  text: 'Schau dir mein Foto an!',
                  url: window.location.href
                });
                // After sharing text, download the image automatically
                downloadImage();
              } catch (textShareError) {
                if (textShareError.name !== 'AbortError') {
                  downloadImage();
                }
              }
            }
          }
        } else if (navigator.share) {
          // Try sharing without files
          try {
            await navigator.share({
              title: 'Mein CityShot',
              text: 'Schau dir mein Foto an!',
              url: window.location.href
            });
            // After sharing text, download the image automatically
            downloadImage();
          } catch (error) {
            if (error.name !== 'AbortError') {
              downloadImage();
            }
          }
        } else {
          // No sharing capabilities, just download
          downloadImage();
        }
      } catch (error) {
        console.error('Error preparing share:', error);
        downloadImage();
      }
      setShareButtonClicked(false);
    }
  };

  // Retake photo
  const retakePhoto = () => {
    setCapturedImage(null);
    startCamera();
  };

  // Helper function to calculate element position and size with relative units
  const getStyleWithRelativeUnits = (posConfig, sizeValue, type = 'default') => {
    const style = { 
      position: 'absolute',
      // Add data attribute to identify overlay elements for scaling
      'data-overlay-element': 'true'
    };

    // Handle horizontal position
    if (posConfig.x === 'left') {
      style.left = '2%';
    } else if (posConfig.x === 'right') {
      style.right = '2%';
    } else if (posConfig.x === 'center') {
      style.left = '50%';
      style.transform = style.transform ? style.transform + ' translateX(-50%)' : 'translateX(-50%)';
    } else if (typeof posConfig.x === 'string' && posConfig.x.endsWith('%')) {
      style.left = posConfig.x;
    } else if (typeof posConfig.x === 'string') {
      style.left = posConfig.x;
    } else if (typeof posConfig.x === 'number') {
      style.left = `${posConfig.x}px`;
    }

    // Handle vertical position
    if (posConfig.y === 'top') {
      style.top = '2%';
    } else if (posConfig.y === 'bottom') {
      style.bottom = '2%';
    } else if (posConfig.y === 'middle') {
      style.top = '50%';
      style.transform = style.transform ? style.transform + ' translateY(-50%)' : 'translateY(-50%)';
    } else if (typeof posConfig.y === 'string' && posConfig.y.endsWith('%')) {
      style.top = posConfig.y;
    } else if (typeof posConfig.y === 'string') {
      style.top = posConfig.y;
    } else if (typeof posConfig.y === 'number') {
      style.top = `${posConfig.y}px`;
    }

    // If both center horizontal and middle vertical
    if (posConfig.x === 'center' && posConfig.y === 'middle') {
      style.transform = 'translate(-50%, -50%)';
    }

    // Handle size based on type
    if (type === 'image') {
      style.height = sizeValue;
      style.width = 'auto';
    } else if (type === 'text') {
      style.fontSize = sizeValue;
    }

    return style;
  };

  // Safari-specific camera permission message
  const renderSafariPermissionMessage = () => {
    if ((browserInfo.isSafari || browserInfo.isIOS || browserInfo.isMacSafari) && cameraPermission === 'denied') {
      return (
        <div className="mt-4 p-4 bg-red-900/50 rounded-lg text-sm text-white">
          <p className="font-bold mb-1">Kamera-Zugriff verweigert</p>
          {browserInfo.isIOS ? (
            <p>Bitte erlauben Sie den Zugriff auf die Kamera in Ihren iOS-Einstellungen unter "Safari &gt; Kamera" und laden Sie die Seite neu.</p>
          ) : browserInfo.isMacSafari ? (
            <p>Bitte erlauben Sie den Zugriff auf die Kamera in Safari unter "Safari &gt; Einstellungen für diese Website &gt; Kamera" und laden Sie die Seite neu.</p>
          ) : (
            <p>Bitte erlauben Sie den Zugriff auf die Kamera in Ihren Browser-Einstellungen und laden Sie die Seite neu.</p>
          )}
        </div>
      );
    }
    return null;
  };

  // Error state - show an error message if the app fails to initialize
  if (initialLoadError) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-red-900/50 rounded-lg p-6 max-w-md text-center">
          <div className="text-red-300 text-6xl mb-4">⚠️</div>
          <h2 className="text-white text-xl font-bold mb-2">Fehler beim Laden der App</h2>
          <p className="text-white/80 mb-4">{initialLoadError}</p>
          <div className="flex flex-col gap-3">
            <button 
              onClick={forceReload} 
              className="bg-white text-gray-900 px-4 py-2 rounded-lg font-medium"
            >
              <SafeIcon icon={FiRefreshCw} className="inline mr-2" />
              Cache leeren und neu laden
            </button>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-gray-700 text-white px-4 py-2 rounded-lg font-medium"
            >
              Seite normal neu laden
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state - detailed progressive loading with feedback
  if (!config || !appLoaded) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center p-4">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-white border-t-transparent mx-auto mb-4"></div>
          <p className="text-white/80">
            {loadingPhase === 'initializing' && 'Initialisiere App...'}
            {loadingPhase === 'detecting-browser' && 'Erkenne Browser...'}
            {loadingPhase === 'loading-config' && 'Lade Konfiguration...'}
            {loadingPhase === 'loading-city-info' && 'Lade Stadtinformationen...'}
            {loadingPhase === 'checking-permissions' && 'Prüfe Berechtigungen...'}
            {loadingPhase === 'getting-location' && 'Ermittle Standort...'}
          </p>
          
          {/* Progressive loading indicators */}
          <div className="mt-4 flex justify-center space-x-2">
            {['initializing', 'detecting-browser', 'loading-config', 'loading-city-info', 'checking-permissions', 'getting-location'].map((phase, index) => (
              <div 
                key={phase}
                className={`h-2 w-2 rounded-full transition-colors duration-300 ${
                  ['initializing', 'detecting-browser'].includes(loadingPhase) && index === 0
                    ? 'bg-blue-400'
                    : ['loading-config', 'loading-city-info'].includes(loadingPhase) && index === 1
                    ? 'bg-blue-400'
                    : ['checking-permissions', 'getting-location'].includes(loadingPhase) && index === 2
                    ? 'bg-blue-400'
                    : 'bg-gray-600'
                }`}
              ></div>
            ))}
          </div>
          
          {/* Provide a reload option for Safari users experiencing issues */}
          {(browserInfo.isSafari || browserInfo.isIOS || browserInfo.isMacSafari) && loadingPhase !== 'initializing' && (
            <button 
              onClick={forceReload} 
              className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
            >
              <SafeIcon icon={FiRefreshCw} className="inline mr-2" />
              Cache leeren und neu laden
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-sm p-4">
        <div className="max-w-md mx-auto text-center">
          <h1 className="text-2xl font-bold text-white">CityShot</h1>
          <p className="text-white/80 text-sm">Erstelle dein perfektes Selfie</p>
          
          {/* Config info for debugging */}
          {configTimestamp && (
            <div className="mt-1 text-xs text-white/50">
              Konfiguration: {new Date(configTimestamp).toLocaleTimeString()} 
              {configSource && ` (${configSource})`}
            </div>
          )}
          
          {/* Clear Cache Button */}
          <button
            onClick={forceReload}
            className="mt-2 bg-blue-600 text-white text-xs px-3 py-1 rounded-md"
          >
            <SafeIcon icon={FiRefreshCw} className="inline mr-1" />
            Cache leeren und neu laden
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex flex-col max-w-md mx-auto w-full p-4">
        {/* Camera/Photo Area */}
        <div 
          ref={photoContainerRef} 
          className="relative bg-black rounded-2xl overflow-hidden mb-6 aspect-square"
          data-photo-container="true"
        >
          {capturedImage ? (
            <img src={capturedImage} alt="Captured" className="w-full h-full object-cover" />
          ) : (
            <>
              <video 
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className={`w-full h-full object-cover ${browserInfo.isSafari || browserInfo.isIOS || browserInfo.isMacSafari ? 'cursor-pointer' : ''}`}
                style={{
                  transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
                  objectFit: 'cover'
                }}
                onClick={() => {
                  // For Safari, clicking on the video can help start playback
                  if ((browserInfo.isSafari || browserInfo.isIOS || browserInfo.isMacSafari) && videoRef.current && !cameraActive) {
                    videoRef.current.play().catch(e => console.error(e));
                  }
                }}
              />

              {/* Camera Overlay */}
              <div ref={overlayRef} className="absolute inset-0 pointer-events-none">
                {/* Logo with relative position and size */}
                {config.logo && (
                  <div 
                    style={{
                      ...getStyleWithRelativeUnits(config.logoPosition, config.logoSize, 'image'),
                      opacity: config.logoOpacity
                    }}
                  >
                    <img 
                      src={config.logo} 
                      alt="Logo" 
                      className="h-full w-auto object-contain" 
                      crossOrigin="anonymous" 
                    />
                  </div>
                )}

                {/* Second image with relative position and size */}
                {config.secondImage && (
                  <div 
                    style={{
                      ...getStyleWithRelativeUnits(config.secondImagePosition, config.secondImageSize, 'image'),
                      opacity: config.secondImageOpacity
                    }}
                  >
                    <img 
                      src={config.secondImage} 
                      alt="Second" 
                      className="h-full w-auto object-contain" 
                      crossOrigin="anonymous" 
                    />
                  </div>
                )}

                {/* Custom Text */}
                {config.customText && (
                  <div 
                    style={{
                      ...getStyleWithRelativeUnits(config.textPosition, config.textSize, 'text'),
                      opacity: config.textOpacity,
                      fontWeight: config.textStyle === 'bold' || config.textStyle === 'bolditalic' ? 'bold' : 'normal',
                      fontStyle: config.textStyle === 'italic' || config.textStyle === 'bolditalic' ? 'italic' : 'normal',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      backgroundColor: config.textBackground ? 'rgba(0,0,0,0.5)' : 'transparent',
                      color: config.textColor || 'white',
                      textAlign: 'center',
                      fontFamily: config.fontFamily || 'Arial',
                      maxWidth: '80%'
                    }}
                  >
                    {config.customText}
                  </div>
                )}

                {/* Location Display with Greeting */}
                {config.showLocation && (
                  <div 
                    style={{
                      ...getStyleWithRelativeUnits(config.locationPosition),
                      backgroundColor: config.textBackground ? 'rgba(0,0,0,0.5)' : 'transparent',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      color: config.textColor || 'white',
                      textAlign: 'center',
                      maxWidth: '80%',
                      opacity: config.locationOpacity,
                      fontFamily: config.fontFamily || 'Arial'
                    }}
                  >
                    {/* Greeting Text */}
                    {config.showGreeting && config.greetingText && (
                      <div style={{
                        fontSize: typeof config.locationSize === 'string' 
                          ? `calc(${config.locationSize} * 0.8)` 
                          : Math.max((config.locationSize || 22) * 0.8, 12) + 'px'
                      }}>
                        {config.greetingText}
                      </div>
                    )}
                    
                    {/* Location Name */}
                    <div 
                      className="flex items-center justify-center gap-2"
                      style={{
                        fontSize: config.locationSize,
                        fontWeight: 'bold'
                      }}
                    >
                      <SafeIcon icon={FiMapPin} />
                      {locationName || (location?.detected ? "Standort wird ermittelt..." : cityInfo?.cityName || "Unbekannt")}
                    </div>
                    
                    {/* Additional Text (e.g., landmark name) */}
                    {config.showAdditionalText && config.additionalText && (
                      <div style={{
                        fontSize: config.additionalTextSize,
                        marginTop: '4px',
                        opacity: config.additionalTextOpacity
                      }}>
                        {config.additionalText}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {!cameraActive && !capturedImage && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
              <div className="text-center text-white p-4">
                <SafeIcon icon={FiCamera} className="text-6xl mb-4 mx-auto opacity-50" />
                <p className="text-lg">Kamera bereit</p>
                <p className="text-sm opacity-70">Tippe auf Start um zu beginnen</p>
                
                {cameraError && (
                  <div className="mt-4 p-3 bg-red-900/50 rounded-lg text-sm text-white">
                    <p className="font-bold mb-1">Kamera-Fehler:</p>
                    <p>{cameraError}</p>
                  </div>
                )}
                
                {renderSafariPermissionMessage()}
                
                {/* Show reload button for iOS/Safari users */}
                {(browserInfo.isSafari || browserInfo.isIOS || browserInfo.isMacSafari) && (
                  <button 
                    onClick={forceReload} 
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
                  >
                    <SafeIcon icon={FiRefreshCw} className="inline mr-2" />
                    Cache leeren und neu laden
                  </button>
                )}
              </div>
            </div>
          )}

          {isCapturing && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent"></div>
            </div>
          )}

          {/* Safari tap overlay - helps with camera activation */}
          {cameraActive && !capturedImage && (browserInfo.isSafari || browserInfo.isIOS || browserInfo.isMacSafari) && (
            <div 
              className="absolute inset-0 z-0" 
              onClick={() => {
                if (videoRef.current) {
                  videoRef.current.play().catch(e => console.error(e));
                }
              }} 
            />
          )}
        </div>

        {/* Controls */}
        <div className="space-y-4">
          {!cameraActive && !capturedImage && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={startCamera}
              className="w-full bg-blue-600 text-white py-4 rounded-2xl font-semibold text-lg flex items-center justify-center gap-3 hover:bg-blue-700 transition-colors"
            >
              <SafeIcon icon={FiCamera} className="text-2xl" />
              Kamera starten
            </motion.button>
          )}

          {cameraActive && !capturedImage && (
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={switchCamera}
                className="flex-1 bg-gray-600 text-white py-4 rounded-2xl font-semibold flex items-center justify-center gap-2 hover:bg-gray-700 transition-colors"
              >
                <SafeIcon icon={FiRotateCcw} />
                Wechseln
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={capturePhoto}
                disabled={isCapturing}
                className={`flex-[2] bg-red-600 text-white py-4 rounded-2xl font-semibold text-lg flex items-center justify-center gap-3 hover:bg-red-700 transition-colors ${isCapturing ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isCapturing ? (
                  <>
                    <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                    Verarbeite...
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiCamera} className="text-2xl" />
                    Foto aufnehmen
                  </>
                )}
              </motion.button>
            </div>
          )}

          {capturedImage && (
            <div className="space-y-3">
              <div className="flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={downloadImage}
                  className="flex-1 bg-green-600 text-white py-4 rounded-2xl font-semibold flex items-center justify-center gap-2 hover:bg-green-700 transition-colors"
                >
                  <SafeIcon icon={FiDownload} />
                  Download
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={shareImage}
                  disabled={shareButtonClicked}
                  className={`flex-1 bg-blue-600 text-white py-4 rounded-2xl font-semibold flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors ${shareButtonClicked ? 'opacity-70' : ''}`}
                >
                  {shareButtonClicked ? (
                    <>
                      <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                      Teilen...
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiShare2} />
                      Teilen
                    </>
                  )}
                </motion.button>
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={retakePhoto}
                className="w-full bg-gray-600 text-white py-3 rounded-2xl font-semibold hover:bg-gray-700 transition-colors"
              >
                Neues Foto aufnehmen
              </motion.button>
            </div>
          )}
        </div>

        {/* Hidden canvas for image processing */}
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {/* Footer */}
      <footer className="bg-white/10 backdrop-blur-sm p-4 text-center">
        <p className="text-white/60 text-sm">
          Powered by CityShot
        </p>
      </footer>
    </div>
  );
};

export default SelfieApp;